/**
 * UT Matrix 전기사양 비교 분석 API
 * REST 엔드포인트(/v1/ut/ut-comparison-elec/*)를 MyBatis Mapper로 직접 실행한다.
 */
import { CommonRequest, CommonResponse, Method, ServiceName } from 'models/common/RestApi';
import { ElecRptDetail, ElecSourceRow, SaveElecRptReq } from 'models/ut/UtComparisonElec';
import { SaveReportResult } from 'models/ut/UtMatrixComparisonReportList';
import { callApi } from 'utils/ApiUtil';
import { toSaveReportResult } from './UtMatrixComparisonReportList';

const utmIdsParams = (utmIds: string[]) => new URLSearchParams(utmIds.map((id) => ['utmIds', id]));

/** 전기 원천 데이터 — 정확히 2개 UT Matrix */
export const findElecDetail = async (utmIds: string[]): Promise<ElecSourceRow[]> => {
  if (utmIds.length === 0) return [];
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/ut/ut-comparison-elec/detail`,
    serviceName: ServiceName.ELM_BE,
    queryParams: utmIdsParams(utmIds),
  };
  const response: CommonResponse<ElecSourceRow[]> = await callApi(request);
  if (response.successOrNot !== 'Y')
    throw new Error(String(response.data ?? '전기 원천 데이터 조회 실패'));
  return response.data ?? [];
};

/**
 * 이력 저장 — ELMAPP(등록 권한) 로그인 시에만 호출되어야 함. 성공 시 RPT_ID를 반환.
 * 동일 조건 리포트가 이미 있으면 BE가 생성하지 않고 기존 RPT_ID를 duplicated 플래그와 함께 돌려준다.
 */
export const saveReport = async (req: SaveElecRptReq): Promise<SaveReportResult> => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/ut/ut-comparison-elec/reports`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: req,
  };
  const response: CommonResponse<any> = await callApi(request);
  if (response.successOrNot !== 'Y') throw new Error(String(response.data ?? '이력 저장 실패'));
  const result = toSaveReportResult(response.data);
  if (!result.rptId) throw new Error('이력 저장 실패 — RPT_ID가 반환되지 않았습니다.');
  return result;
};

/** 저장된 이력 상세 조회 — UtMatrixComparisonReportListPage에서 구분=전기 행 클릭 시 사용 */
export const findReportDetail = async (rptId: string): Promise<ElecRptDetail> => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/ut/ut-comparison-elec/reports/${encodeURIComponent(rptId)}`,
    serviceName: ServiceName.ELM_BE,
  };
  const response: CommonResponse<ElecRptDetail> = await callApi(request);
  if (response.successOrNot !== 'Y')
    throw new Error(String(response.data ?? `이력 조회 실패 (RPT_ID: ${rptId})`));
  if (!response.data) throw new Error(`이력을 찾을 수 없습니다 (RPT_ID: ${rptId})`);
  return response.data;
};

/** 전체 종합의견(RMK)만 경량 조회 — ElecOpinionPanel이 historyRptId 로드 시 사용 */
export const findRmk = async (rptId: string): Promise<string> => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/ut/ut-comparison-elec/reports/${encodeURIComponent(rptId)}/rmk`,
    serviceName: ServiceName.ELM_BE,
  };
  const response: CommonResponse<string> = await callApi(request);
  if (response.successOrNot !== 'Y') throw new Error(String(response.data ?? 'RMK 조회 실패'));
  return response.data ?? '';
};

/** 전체 종합의견(RMK) 저장 */
export const updateRmk = async (rptId: string, rmk: string, userId: string): Promise<void> => {
  const request: CommonRequest = {
    method: Method.PUT,
    url: `/v1/ut/ut-comparison-elec/reports/${encodeURIComponent(rptId)}/rmk`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: { rmk, userId },
  };
  const response: CommonResponse<void> = await callApi(request);
  if (response.successOrNot !== 'Y') throw new Error(String(response.data ?? 'RMK 저장 실패'));
};
